// Tlačítko „Nástroje" v horní liště — Phoenix-native akce desktopu. Viditelnost se řídí Phoenix
// oprávněními (`tools.*` z `getMe`), takže tlačítko i položky jsou známé hned po přihlášení a nečekají
// na načtení Magicu. Akce, které ke svému provedení Magic potřebují, jsou do jeho načtení jen disabled.
// Form-otevírací nástroje (Logs, Daemons) přes `sendMagicOpenForm`; ostatní (debug stav/toggle, debug
// konzole, vyprázdnit keše, restart/kill) přes `sendMagicDesktopRequest('desktop/dev-op', { op })`
// (async request/reply do control iframu). „Obnovit keš oprávnění" je čistě Phoenix (bez Magicu).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { refreshPermissionCache } from '@/api/generated/auth/auth';
import { useGetDevelopmentDatabaseInfo } from '@/api/generated/ping/ping';
import { testMagicConnection, type MagicConnectionTestResponse } from '@/api/magicIntegration';
import {
  sendMagicDesktopRequest,
  sendMagicOpenForm,
  setPendingMenuOpen,
  useMagicMenu,
} from '@/shared/legacyMagicBridge';
import { useAuth, userHasPermission } from '@/shared/auth';
import { toast } from '@/shared/ui';
import { useWindowOnboardingStore } from '@/modules/window-onboarding';
import { TOOLS_PERMISSIONS } from './toolsPermissions';
import { Button } from '@/shared/ui/button';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import { Dialog } from '@/shared/ui/dialog';
import { DropdownMenu, MenuDivider, MenuItem } from '@/shared/ui/dropdown-menu';
import { Icon } from '@/shared/ui/icon';

const DEV_OP = 'desktop/dev-op';

/** Bezpečně dekóduje HTML entity z obsahu debug konzole (Magic ho vrací HTML-encoded). */
function decodeHtml(value: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
}

/** Zkopíruje text do schránky — moderní Clipboard API + fallback pro ne-secure kontext. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // padá do fallbacku níže
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

export function DesktopToolsButton() {
  const { t } = useTranslation('common');
  const { t: tOnboarding } = useTranslation('windowOnboarding');
  const openWizard = useWindowOnboardingStore((s) => s.openWizard);
  const auth = useAuth();
  // Magic je „ready" (lze dispatchovat dev-ops do control iframu), jakmile dorazilo menu-ready.
  const magicReady = useMagicMenu().isReady;
  // Development prostředí (Phoenix-native, cachované — stejný dotaz jako DevelopmentDatabaseBadge).
  const { data: envInfo } = useGetDevelopmentDatabaseInfo({
    query: { staleTime: Infinity, retry: false },
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [debugOn, setDebugOn] = useState<boolean | null>(null);
  const [consoleContent, setConsoleContent] = useState<string | null>(null);
  const [cacheDone, setCacheDone] = useState(false);
  const [permDone, setPermDone] = useState(false);
  const [confirmOp, setConfirmOp] = useState<'restart' | 'kill' | null>(null);
  const [magicTest, setMagicTest] = useState<MagicConnectionTestResponse | null>(null);

  // Viditelnost řídí Phoenix oprávnění (z `getMe`) — známá hned po přihlášení, nečeká se na Magic.
  // Config super-user vidí všechny nástroje vázané na oprávnění (viz userHasPermission).
  const user = auth.status === 'authenticated' ? auth.user : null;
  const canLogs = userHasPermission(user, TOOLS_PERMISSIONS.logs);
  const canDebug = userHasPermission(user, TOOLS_PERMISSIONS.debug);
  const canCache = userHasPermission(user, TOOLS_PERMISSIONS.cache);
  const canRefreshPerms = userHasPermission(user, TOOLS_PERMISSIONS.refreshPermissions);
  // Test spojení s Magicem je striktně jen pro config uživatele (nevázané na běžná tools.* oprávnění).
  const isConfig = !!user?.isConfig;
  // Dev-ops (démoni/restart/kill) bez oprávnění — automaticky jen v Development prostředí.
  const isDevEnv = envInfo?.environmentName === 'Development';

  // Položky vázané na oprávnění (nebo Dev prostředí). Průvodce okny je vždy dostupný (bez oprávnění),
  // takže tlačítko „Nástroje" se zobrazuje vždy — uživatel se k průvodci kdykoliv vrátí přes toto menu.
  const hasGatedItems = canLogs || canDebug || canCache || canRefreshPerms || isDevEnv;

  // Akce, které ke svému provedení potřebují Magic, jsou do jeho načtení disabled (ať se set položek
  // „neskáče" — viditelnost je stabilní z oprávnění, mění se jen enabled stav). Tooltip vysvětlí proč.
  const magicHint = !magicReady ? t('tools.waitingForMagic') : undefined;

  const runOp = async (op: string): Promise<Record<string, unknown> | null> => {
    setBusy(true);
    try {
      return await sendMagicDesktopRequest(DEV_OP, { op });
    } catch {
      toast({ severity: 'error', description: t('tools.opFailed') });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Stav debugu zjistíme z Magicu jen když je dostupný a uživatel má debug oprávnění.
    if (next && canDebug && magicReady && debugOn === null) {
      void sendMagicDesktopRequest(DEV_OP, { op: 'debug-get' })
        .then((d) => setDebugOn(Boolean(d['debug'])))
        .catch(() => undefined);
    }
  };

  const openForm = (form: string, title: string) => {
    setPendingMenuOpen('detached', title);
    sendMagicOpenForm(form);
    setOpen(false);
  };

  const toggleDebug = () => {
    void runOp('debug-toggle').then((d) => {
      if (d) setDebugOn(Boolean(d['debug']));
    });
  };

  const loadConsole = () => {
    void runOp('debug-console').then((d) => {
      if (d) setConsoleContent(decodeHtml(String(d['content'] ?? '')));
    });
  };

  const openConsole = () => {
    setOpen(false);
    loadConsole();
  };

  const copyConsole = () => {
    void copyToClipboard(consoleContent ?? '').then((ok) =>
      toast(
        ok
          ? { severity: 'success', description: t('tools.copied') }
          : { severity: 'error', description: t('tools.copyFailed') }
      )
    );
  };

  const clearCache = () => {
    setOpen(false);
    void runOp('clear-cache').then((d) => {
      if (d) setCacheDone(true);
    });
  };

  // Čistě Phoenix nástroj — hromadně obnoví keš oprávnění (role i uživatel→role) přes Phoenix endpoint.
  const refreshPerms = () => {
    setOpen(false);
    setBusy(true);
    void refreshPermissionCache()
      .then(() => setPermDone(true))
      .catch(() => toast({ severity: 'error', description: t('tools.opFailed') }))
      .finally(() => setBusy(false));
  };

  // Otestuje přímý server-to-server kanál Phoenix → Magic. Nezávislé na načtení Magic controlu v iframu
  // (netestuje postMessage most, ale backend → Magic .ws). Výsledek (úspěch i chybu) ukáže dialog.
  const testMagic = () => {
    setOpen(false);
    setBusy(true);
    void testMagicConnection()
      .then((r) => setMagicTest(r))
      .catch((e: unknown) =>
        setMagicTest({ success: false, error: e instanceof Error ? e.message : String(e) })
      )
      .finally(() => setBusy(false));
  };

  const askConfirm = (op: 'restart' | 'kill') => {
    setOpen(false);
    setConfirmOp(op);
  };

  // Průvodce prací s okny — čistě Phoenix, bez oprávnění a bez závislosti na Magicu.
  const openGuide = () => {
    setOpen(false);
    openWizard();
  };

  return (
    <>
      <DropdownMenu
        trigger={
          <button
            type="button"
            className="rounded-phx-sm text-phx-text-2 hover:bg-phx-surface-2 h-phx-8 w-phx-control-lg flex cursor-pointer items-center justify-center border-0 bg-transparent"
            title={t('tools.title')}
            aria-label={t('tools.title')}
          >
            <Icon name="wrench" size={16} />
          </button>
        }
        align="end"
        backdrop
        open={open}
        onOpenChange={handleOpenChange}
        className="p-phx-1 w-[240px]"
      >
        {/* Průvodce prací s okny — vždy dostupný (bez oprávnění), proto první a nezávislý na Magicu. */}
        <MenuItem icon="layers" onClick={openGuide}>
          {tOnboarding('menuItem')}
        </MenuItem>
        {hasGatedItems && <MenuDivider />}
        {canLogs && (
          <MenuItem
            icon="list"
            disabled={!magicReady}
            title={magicHint}
            onClick={() => openForm('External.Logs', t('tools.logs'))}
          >
            {t('tools.logs')}
          </MenuItem>
        )}
        {canDebug && (
          <MenuItem
            icon="cog"
            disabled={busy || !magicReady}
            title={magicHint}
            hint={debugOn === null ? '…' : debugOn ? 'ON' : 'OFF'}
            onClick={toggleDebug}
          >
            {t('tools.debug')}
          </MenuItem>
        )}
        {canDebug && (
          <MenuItem
            icon="doc"
            disabled={busy || !magicReady}
            title={magicHint}
            onClick={openConsole}
          >
            {t('tools.debugConsole')}
          </MenuItem>
        )}
        {canCache && (
          <MenuItem
            icon="refresh"
            disabled={busy || !magicReady}
            title={magicHint}
            onClick={clearCache}
          >
            {t('tools.clearCache')}
          </MenuItem>
        )}
        {/* Čistě Phoenix akce — funguje i bez Magicu, proto se nedisableuje při jeho nedostupnosti. */}
        {canRefreshPerms && (
          <MenuItem icon="lock" disabled={busy} onClick={refreshPerms}>
            {t('tools.refreshPermissions')}
          </MenuItem>
        )}
        {/* Test přímého S2S spojení s Magicem — jen config; nezávislé na načtení Magic controlu. */}
        {isConfig && (
          <MenuItem icon="link" disabled={busy} onClick={testMagic}>
            {t('tools.testMagicConnection')}
          </MenuItem>
        )}
        {isDevEnv && (
          <MenuItem
            iconNode={
              <span className="text-phx-text-muted h-phx-2_5 w-phx-2_5 block rounded-full bg-current opacity-40" />
            }
            disabled={!magicReady}
            title={magicHint}
            onClick={() => openForm('Menu.DaemonStates', t('tools.daemons'))}
          >
            {t('tools.daemons')}
          </MenuItem>
        )}
        {isDevEnv && (
          <>
            <MenuDivider />
            <MenuItem
              icon="skull"
              tone="danger"
              disabled={busy || !magicReady}
              title={magicHint}
              onClick={() => askConfirm('restart')}
            >
              {t('tools.restart')}
            </MenuItem>
            <MenuItem
              icon="skull"
              tone="danger"
              disabled={busy || !magicReady}
              title={magicHint}
              onClick={() => askConfirm('kill')}
            >
              {t('tools.kill')}
            </MenuItem>
          </>
        )}
      </DropdownMenu>

      {consoleContent !== null && (
        <Dialog
          open
          onClose={() => setConsoleContent(null)}
          title={t('tools.debugConsole')}
          width="xl"
          footer={
            <>
              <Button variant="outline" size="md" onClick={loadConsole} disabled={busy}>
                {t('tools.refresh')}
              </Button>
              <Button variant="primary" size="md" onClick={() => setConsoleContent(null)}>
                {t('close')}
              </Button>
            </>
          }
        >
          <div className="relative">
            <button
              type="button"
              onClick={copyConsole}
              title={t('tools.copy')}
              aria-label={t('tools.copy')}
              className="rounded-phx-sm border-phx-border bg-phx-surface text-phx-text-2 hover:bg-phx-surface-2 shadow-phx-sm absolute top-2 right-5 z-10 inline-flex h-7 w-7 cursor-pointer items-center justify-center border"
            >
              <Icon name="copy" size={14} />
            </button>
            <pre className="bg-phx-surface-2 rounded-phx text-phx-text-2 py-phx-3 pl-phx-3 text-phx-xs max-h-[60vh] overflow-auto pr-12 leading-snug break-words whitespace-pre-wrap">
              {consoleContent || '—'}
            </pre>
          </div>
        </Dialog>
      )}

      {cacheDone && (
        <Dialog
          open
          onClose={() => setCacheDone(false)}
          title={t('tools.clearCache')}
          description={t('tools.cacheCleared')}
          icon="ok"
          tone="success"
          width="sm"
          footer={
            <Button variant="primary" size="md" onClick={() => setCacheDone(false)}>
              OK
            </Button>
          }
        />
      )}

      {permDone && (
        <Dialog
          open
          onClose={() => setPermDone(false)}
          title={t('tools.refreshPermissions')}
          description={t('tools.permissionsRefreshed')}
          icon="ok"
          tone="success"
          width="sm"
          footer={
            <Button variant="primary" size="md" onClick={() => setPermDone(false)}>
              OK
            </Button>
          }
        />
      )}

      {magicTest && (
        <Dialog
          open
          onClose={() => setMagicTest(null)}
          title={t('tools.magicConnectionTitle')}
          description={
            magicTest.success
              ? t('tools.magicConnectionOk', {
                  userName: magicTest.userName ?? '',
                  userId: magicTest.userId ?? '',
                  version: magicTest.version ?? '',
                })
              : t('tools.magicConnectionFailed', { error: magicTest.error ?? '' })
          }
          icon={magicTest.success ? 'ok' : 'alert'}
          tone={magicTest.success ? 'success' : 'danger'}
          width="sm"
          footer={
            <Button variant="primary" size="md" onClick={() => setMagicTest(null)}>
              OK
            </Button>
          }
        />
      )}

      {confirmOp && (
        <ConfirmDialog
          title={t(`tools.${confirmOp}`)}
          message={t(`tools.${confirmOp}Confirm`)}
          confirmLabel={t(`tools.${confirmOp}`)}
          cancelLabel={t('cancel')}
          onConfirm={() => {
            const op = confirmOp;
            setConfirmOp(null);
            void runOp(op);
          }}
          onCancel={() => setConfirmOp(null)}
        />
      )}
    </>
  );
}
